/**
 * PwaManager — MobX store для управління станом PWA
 *
 * Відстежує:
 * - isOffline: чи відсутнє мережеве з'єднання
 * - updateAvailable: чи є нова версія Service Worker
 * - canInstall: чи можна запропонувати встановлення
 * - isInstalled: чи вже встановлено як PWA
 *
 * Не залежить від Core. Реєструється тільки коли pwa.enabled = true.
 */

import { makeAutoObservable } from 'mobx'

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
    prompt(): Promise<void>
}

class PwaManager {
    isOffline: boolean = !navigator.onLine
    updateAvailable: boolean = false
    isInstalled: boolean = false

    private _installPromptEvent: BeforeInstallPromptEvent | null = null
    private _waitingWorker: ServiceWorker | null = null

    constructor() {
        makeAutoObservable(this)
        this._bindNetworkEvents()
        this._bindInstallEvents()
        this._detectStandaloneMode()
    }

    get canInstall(): boolean {
        return this._installPromptEvent !== null && !this.isInstalled
    }

    setUpdateAvailable(value: boolean): void {
        this.updateAvailable = value
    }

    setWaitingWorker(worker: ServiceWorker | null): void {
        this._waitingWorker = worker
    }

    applyUpdate(): void {
        const waiting = this._waitingWorker
        if (!waiting || !('serviceWorker' in navigator)) {
            window.location.reload()
            return
        }

        // Перезавантажуємо лише ПІСЛЯ того, як новий SW став контролером.
        // Раніше reload йшов одразу за postMessage: навігацію ще обслуговував
        // старий SW, сторінка діставала старий бандл із його precache, і водію
        // доводилось тиснути «Оновити» вдруге. Таймаут — запобіжник, якщо
        // controllerchange з якоїсь причини не прийде.
        let reloaded = false
        const reloadOnce = () => {
            if (reloaded) return
            reloaded = true
            window.location.reload()
        }
        navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true })
        setTimeout(reloadOnce, 5000)

        waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    async promptInstall(): Promise<boolean> {
        if (!this._installPromptEvent) return false

        await this._installPromptEvent.prompt()
        const { outcome } = await this._installPromptEvent.userChoice

        if (outcome === 'accepted') {
            this.isInstalled = true
        }
        this._installPromptEvent = null
        return outcome === 'accepted'
    }

    private _bindNetworkEvents(): void {
        window.addEventListener('online', () => {
            this.isOffline = false
        })
        window.addEventListener('offline', () => {
            this.isOffline = true
        })
    }

    private _bindInstallEvents(): void {
        window.addEventListener('beforeinstallprompt', (e: Event) => {
            e.preventDefault()
            this._installPromptEvent = e as BeforeInstallPromptEvent
        })
        window.addEventListener('appinstalled', () => {
            this.isInstalled = true
            this._installPromptEvent = null
        })
    }

    private _detectStandaloneMode(): void {
        // Check if already running as installed PWA
        const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true
        if (isStandalone) {
            this.isInstalled = true
        }

        // Handle launches directed at this existing PWA window (launch_handler: focus-existing).
        // When Chrome focuses an already-open PWA window instead of opening a new one,
        // the target URL is enqueued here rather than causing a full navigation.
        // We trigger a client-side navigation so the router handles the URL normally.
        if ('launchQueue' in window) {
            (window as any).launchQueue.setConsumer((launchParams: { targetURL: string }) => {
                if (launchParams.targetURL) {
                    const url = new URL(launchParams.targetURL)
                    // Only navigate if it's a different path to avoid redundant re-renders
                    if (url.pathname !== window.location.pathname || url.search !== window.location.search) {
                        window.history.pushState({}, '', url.pathname + url.search + url.hash)
                        window.dispatchEvent(new PopStateEvent('popstate'))
                    }
                }
            })
        }
    }
}

export const pwaManager = new PwaManager()
